import { gridDim, gridSel, type GridLayout } from './api';

/**
 * Pilihan tata letak dihitung dari bentuk layar, bukan dibaca dari tabel
 * breakpoint (§G8, 23 Agu 2026).
 *
 * Sampai versi ini pilihan grid datang dari tangga lebar layar: <640, <1024,
 * <1920, selebihnya. Tangga itu menjawab "layarnya seberapa lebar" padahal yang
 * menentukan grid mana yang muat adalah **bentuknya**. Dua layar selebar 1280px
 * bisa punya tinggi 800 dan 400, dan tata letak yang mengisi penuh salah satunya
 * menyisakan setengah layar kosong di satunya. Ponsel lipat, ultrawide, iPad
 * yang diputar, dan TV 32:9 semuanya jatuh di antara anak tangga.
 *
 * Yang dihitung di sini satu angka: berapa bagian kotak yang tersedia benar-benar
 * tertutup gambar. Karena rasio tile terkunci 16:9 (§G7 no.4), grid C×R punya
 * rasio `C×16 / R×9`, dan ia mengisi penuh hanya kalau rasio itu sama dengan
 * rasio kotaknya. Semakin jauh, semakin banyak batang kosong di sisi atau di
 * bawah — dan di ruang monitor, ruang kosong berarti gambar yang lebih kecil
 * daripada yang sebenarnya bisa didapat.
 */

/** Rasio tile, terkunci §G7 no.4. */
const RASIO_TILE = 16 / 9;

/**
 * Katalog tata letak. `kolom×baris`, seperti sepanjang §G8.
 *
 * Isinya sengaja mencakup tiga keluarga bentuk — tinggi (ponsel/tablet potret),
 * bujur sangkar (monitor 16:9), dan lebar (ultrawide, ponsel yang diputar) —
 * karena penyaringnya yang memutuskan mana yang muncul, bukan daftar ini.
 * Menambah satu entri di sini tidak pernah membuat pilihan bertambah di layar
 * yang tidak cocok untuknya.
 */
export const KATALOG: readonly GridLayout[] = [
  '1x1',
  '1x2',
  '1x3',
  '1x4',
  '2x1',
  '2x2',
  '2x3',
  '2x4',
  '3x2',
  '3x3',
  '3x4',
  '4x3',
  '4x4',
  '4x5',
  '5x4',
  '5x5',
  '6x5',
];

/**
 * Lebar tile paling kecil yang masih bisa dibaca, dalam piksel CSS.
 *
 * Angkanya dari pengukuran §G8 sendiri: pada layar 390px, tata letak dua kolom
 * memberi tile selebar ±190px dan **nama kamera di bawahnya tidak terbaca**.
 * 220px menaruh batasnya tepat di atas kegagalan yang sudah terbukti itu.
 *
 * Ini pagar yang terpisah dari perhitungan isian, dan harus begitu: 4×3 pada
 * ponsel yang diputar mengisi 98% layar — angka terbaik di daftarnya — dengan
 * dua belas tile selebar 207px yang tak satu pun bisa dikenali. Penuh tidak sama
 * dengan terbaca.
 */
const LEBAR_TILE_MIN = 220;

/** Di bawah ini, batang kosongnya lebih menonjol daripada gambarnya. */
const ISIAN_MIN = 0.5;

/** Banyaknya tombol yang boleh berjajar di bilah kendali sekaligus. */
const MAKS_PILIHAN = 6;

/** Lebar tile yang dihasilkan tata letak ini di dalam kotak `w × h`. */
function lebarTile(l: GridLayout, w: number, h: number): number {
  const { kolom, baris } = gridDim(l);
  // Yang menentukan adalah sisi yang lebih dulu kehabisan: entah lebarnya
  // dibagi kolom, atau tingginya dibagi baris lalu dikembalikan ke lebar lewat
  // rasio tile.
  return Math.min(w / kolom, (h / baris) * RASIO_TILE);
}

/**
 * Berapa bagian kotak `w × h` yang tertutup gambar, 0–1.
 *
 * 1 berarti tidak ada satu piksel pun yang terbuang; 0,5 berarti separuh layar
 * jadi batang kosong.
 */
export function isian(l: GridLayout, w: number, h: number): number {
  if (w <= 0 || h <= 0) return 0;
  const { kolom, baris } = gridDim(l);
  const tile = lebarTile(l, w, h);
  return ((tile * kolom) * ((tile / RASIO_TILE) * baris)) / (w * h);
}

/**
 * Jumlah sel yang dituju sebagai **nilai awal** untuk sebuah layar.
 *
 * Ini satu-satunya bagian yang masih membaca ukuran layar apa adanya, dan
 * memang harus: angka-angka ini keputusan operasional, bukan geometri.
 * TV pos satpam sengaja 4 tile besar dan bukan 16 tile kecil — dari jarak
 * beberapa meter yang pertama jauh lebih terbaca, dan bandwidth-nya seperempat
 * (§G8.1). Yang berubah 23 Agu 2026 hanya **pilihannya**; nilai awalnya tetap
 * seperti yang sudah disepakati.
 */
function selTujuan(w: number, h: number): number {
  if (w < 640 && h >= w) return 2; // ponsel tegak
  if (h < 640 && w < 1024) return 2; // ponsel diputar
  if (w < 1024) return 4; // tablet
  if (w < 1920) return 9; // desktop
  return 4; // TV pos satpam (§G8.1)
}

/**
 * Pilihan tata letak untuk kotak `w × h`, sudah diurutkan menurut jumlah sel.
 *
 * Urutan kerjanya:
 * 1. buang yang tilenya terlalu kecil untuk dibaca;
 * 2. buang yang meninggalkan lebih banyak ruang kosong daripada gambar;
 * 3. dari tiap **jumlah sel** simpan satu yang paling penuh — 3×2 dan 2×3
 *    sama-sama enam tile, dan menawarkan keduanya berarti meminta orang memilih
 *    antara dua hal yang perbedaannya tidak pernah mereka pikirkan;
 * 4. ambil yang paling penuh sebanyak `MAKS_PILIHAN`, lalu urutkan menurut
 *    jumlah sel supaya tombolnya naik teratur dari sedikit ke banyak.
 *
 * Kalau seluruh katalog tersaring habis — layar yang sangat kecil atau sangat
 * ganjil — dua yang paling penuh tetap dikembalikan. Bilah kendali dengan satu
 * tombol adalah bilah yang tidak melakukan apa-apa.
 */
export function pilihanUntuk(w: number, h: number): GridLayout[] {
  const dinilai = KATALOG.map((l) => ({ l, isi: isian(l, w, h), tile: lebarTile(l, w, h) }));

  const lolos = dinilai.filter((x) => x.tile >= LEBAR_TILE_MIN && x.isi >= ISIAN_MIN);

  const terbaikPerSel = new Map<number, { l: GridLayout; isi: number }>();
  for (const x of lolos) {
    const sel = gridSel(x.l);
    const ada = terbaikPerSel.get(sel);
    if (!ada || x.isi > ada.isi) terbaikPerSel.set(sel, x);
  }

  const pilihan = [...terbaikPerSel.values()]
    .sort((a, b) => b.isi - a.isi)
    .slice(0, MAKS_PILIHAN)
    .map((x) => x.l);

  if (pilihan.length >= 2) return pilihan.sort((a, b) => gridSel(a) - gridSel(b));

  return dinilai
    .sort((a, b) => b.isi - a.isi)
    .slice(0, 2)
    .map((x) => x.l)
    .sort((a, b) => gridSel(a) - gridSel(b));
}

/**
 * Tata letak ini masih layak dipakai di kotak `w × h`? (24 Agustus 2026)
 *
 * Ini **syarat**, sedangkan `pilihanUntuk` di atas adalah **daftar pendek**:
 * yang pertama menjawab "boleh?", yang kedua menjawab "enam mana yang
 * ditawarkan lebih dulu?". Keduanya sempat dipakai sebagai satu hal, dan itu
 * yang membuat tata letak pilihan orang hilang begitu kotaknya berubah:
 * 4×5 di kotak 1920×1080 mengisi 80% dengan tile selebar 384px — jelas layak —
 * tapi ia tidak masuk enam besar karena tata letak bujur sangkar mengisi 100%
 * di kotak 16:9. Pilihan yang sah tidak boleh dibatalkan hanya karena ada enam
 * yang lebih penuh.
 */
export function sahUntuk(l: GridLayout, w: number, h: number): boolean {
  return lebarTile(l, w, h) >= LEBAR_TILE_MIN && isian(l, w, h) >= ISIAN_MIN;
}

/**
 * Daftar tombol yang benar-benar digambar: daftar pendek **plus** tata letak
 * yang sedang dipakai (24 Agustus 2026).
 *
 * Pemilih yang tidak memuat pilihan yang sedang aktif adalah pemilih yang
 * berbohong — tidak ada satu tombol pun yang menyala, dan tidak ada cara
 * kembali ke tata letak itu sesudah menekan yang lain.
 *
 * Batas enam tetap dijaga (bilah dashboard harus muat sebaris, §G8.2): yang
 * disingkirkan untuk memberi tempat adalah tetangga terdekatnya menurut jumlah
 * sel — pilihan yang paling banyak diduplikasi oleh sisipan ini.
 */
export function pilihanDengan(l: GridLayout, pilihan: GridLayout[]): GridLayout[] {
  if (pilihan.includes(l)) return pilihan;
  const sel = gridSel(l);
  const tetangga = [...pilihan].sort(
    (a, b) => Math.abs(gridSel(a) - sel) - Math.abs(gridSel(b) - sel),
  )[0];
  const sisa =
    pilihan.length < MAKS_PILIHAN ? pilihan : pilihan.filter((x) => x !== tetangga);
  return [...sisa, l].sort((a, b) => gridSel(a) - gridSel(b));
}

/** Nilai awal untuk kotak ini: pilihan yang jumlah selnya paling dekat ke tujuan. */
export function bawaanUntuk(w: number, h: number, pilihan = pilihanUntuk(w, h)): GridLayout {
  const tujuan = selTujuan(w, h);
  return [...pilihan].sort((a, b) => {
    const da = Math.abs(gridSel(a) - tujuan);
    const db = Math.abs(gridSel(b) - tujuan);
    // Seri dimenangkan yang lebih sedikit selnya: tile besar lebih terbaca, dan
    // di antara dua yang sama-sama meleset itu yang lebih aman.
    return da - db || gridSel(a) - gridSel(b);
  })[0]!;
}
